import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { FiChevronRight } from "react-icons/fi";
import { FaCaretDown, FaPenToSquare } from "react-icons/fa6";

import "./AttendanceHome.css";

/*
 * Presentational attendance screen shared by the user & admin mobile pages.
 * All data / behaviour stays in the page; this only renders it.
 */
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT = MONTHS.map((m) => m.slice(0, 3));

const FIRST_YEAR = 2012;

const LEGEND = [
  { key: "present", label: "Present" },
  { key: "absent", label: "Absent" },
  { key: "leave", label: "Leave" },
  { key: "holiday", label: "Holiday" },
  { key: "weekoff", label: "Week Off" },
];

// "Mon, 28 Sept 2026" as in the design
const formatLogDate = (date) => {
  const m = moment(date);
  const month = m.format("MMM") === "Sep" ? "Sept" : m.format("MMM");
  return `${m.format("ddd, DD")} ${month} ${m.format("YYYY")}`;
};

// "HH:mm[:ss]" -> "9:30 AM"
const formatClock = (time) =>
  time ? moment(time, ["HH:mm:ss", "HH:mm"]).format("h:mm A") : "-";

// punch datetime -> "9:35 AM"
const formatPunch = (dateTime) =>
  dateTime ? moment(dateTime).format("h:mm A") : "-";

const AttendanceHome = ({
  date,
  onDateChange,
  activeStartDate,
  onActiveStartDateChange,
  tileClassName,
  shiftDetails,
  todayLog,
  isRegularizeDisabled,
  onRegularize,
  onHistoryClick,
  onTeamClick,
}) => {
  const currentYear = new Date().getFullYear();

  const [showPicker, setShowPicker] = useState(false);
  const [expandedYear, setExpandedYear] = useState(currentYear);
  const pickerRef = useRef(null);
  const topBarRef = useRef(null);

  const years = [];
  for (let year = FIRST_YEAR; year <= currentYear; year++) years.push(year);

  // open the picker scrolled to the current year
  useEffect(() => {
    if (showPicker && pickerRef.current) {
      pickerRef.current.scrollTop = pickerRef.current.scrollHeight;
    }
  }, [showPicker]);

  // close the picker on an outside tap
  useEffect(() => {
    if (!showPicker) return undefined;

    const handleOutside = (e) => {
      if (topBarRef.current && !topBarRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [showPicker]);

  const handleMonthSelect = (year, monthIndex) => {
    const newDate = new Date(year, monthIndex, 1);
    onDateChange(newDate);
    onActiveStartDateChange(newDate);
    setShowPicker(false);
  };

  const isOffDay =
    shiftDetails.type === "Weekoff" || shiftDetails.type === "Holiday";

  const shiftLine = isOffDay
    ? shiftDetails.message || shiftDetails.type
    : shiftDetails.shift_start_time && shiftDetails.shift_end_time
      ? `${shiftDetails.shift_name ? `${shiftDetails.shift_name} Shift: ` : ""}${formatClock(
          shiftDetails.shift_start_time,
        )} - ${formatClock(shiftDetails.shift_end_time)}`
      : "No shift assigned";

  const monthLabel =
    activeStartDate.getFullYear() === currentYear
      ? MONTHS[activeStartDate.getMonth()]
      : `${MONTHS[activeStartDate.getMonth()]} ${activeStartDate.getFullYear()}`;

  return (
    <div className="ah-page">
      {/* CALENDAR */}
      <section
        className={`ah-card ah-card-glass ah-calendar-card ${
          showPicker ? "picker-open" : ""
        }`}
      >
        <div className="ah-calendar-top" ref={topBarRef}>
          <button
            type="button"
            className="ah-month-btn"
            aria-expanded={showPicker}
            onClick={() => setShowPicker(!showPicker)}
          >
            {monthLabel}
            <FaCaretDown className={`ah-month-caret ${showPicker ? "open" : ""}`} />
          </button>

          {showPicker && (
            <div className="ah-month-picker" ref={pickerRef}>
              {years.map((year) => (
                <div key={year}>
                  <button
                    type="button"
                    className="ah-year-title"
                    onClick={() =>
                      year !== currentYear &&
                      setExpandedYear(expandedYear === year ? null : year)
                    }
                  >
                    {year}
                  </button>

                  {(year === currentYear || expandedYear === year) && (
                    <div className="ah-month-grid">
                      {MONTH_SHORT.map((month, index) => {
                        const active =
                          activeStartDate.getMonth() === index &&
                          activeStartDate.getFullYear() === year;

                        return (
                          <button
                            type="button"
                            key={`${year}-${index}`}
                            className={`ah-month-cell ${active ? "active" : ""}`}
                            onClick={() => handleMonthSelect(year, index)}
                          >
                            {month}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Calendar
          className="ah-calendar"
          value={date}
          onChange={onDateChange}
          activeStartDate={activeStartDate}
          onActiveStartDateChange={({ activeStartDate: next }) =>
            onActiveStartDateChange(next)
          }
          locale="en-US"
          formatShortWeekday={(locale, d) =>
            ["S", "M", "T", "W", "T", "F", "S"][d.getDay()]
          }
          tileClassName={tileClassName}
          showNeighboringMonth
          showNavigation={false}
        />

        <div className="ah-legend">
          {LEGEND.map((item) => (
            <span key={item.key} className="ah-legend-item">
              <span className={`ah-legend-dot ah-dot-${item.key}`} />
              {item.label}
            </span>
          ))}
        </div>
      </section>

      {/* DAY LOG */}
      <section className="ah-card ah-card-glass ah-log">
        <p className="ah-log-title">{formatLogDate(date)} Log</p>
        <p className="ah-log-label">Office Timings</p>
        <p className="ah-log-shift">{shiftLine}</p>

        <div className="ah-punch-row">
          <div className="ah-punch-box">
            <span className="ah-punch-label">Punch In</span>
            <span className="ah-punch-value">
              {formatPunch(todayLog.first_punch)}
            </span>
          </div>

          <div className="ah-punch-box">
            <span className="ah-punch-label">Punch Out</span>
            <span className="ah-punch-value">
              {formatPunch(todayLog.last_punch)}
            </span>
          </div>
        </div>
      </section>

      {/* REGULARIZE */}
      <button
        type="button"
        className={`ah-card ah-card-glass ah-regularize ${
          isRegularizeDisabled ? "disabled" : ""
        }`}
        onClick={onRegularize}
        disabled={isRegularizeDisabled}
      >
        <span className="ah-icon-box">
          <span className="ah-icon-inner">
            <FaPenToSquare />
          </span>
        </span>

        <span className="ah-regularize-text">
          <span className="ah-regularize-title">
            Need to update your attendance?
          </span>
          <span className="ah-regularize-sub">Regularize attendance</span>
        </span>

        <span className="ah-arrow">
          <FiChevronRight />
        </span>
      </button>

      {/* LINKS */}
      <button type="button" className="ah-link-btn" onClick={onHistoryClick}>
        <span className="ah-link-bubble">
          <span className="ah-link-dot" />
        </span>
        <span className="ah-link-text">Attendance History</span>
        <span className="ah-arrow">
          <FiChevronRight />
        </span>
      </button>

      <button type="button" className="ah-link-btn" onClick={onTeamClick}>
        <span className="ah-link-bubble">
          <span className="ah-link-dot" />
        </span>
        <span className="ah-link-text">Team’s Attendance</span>
        <span className="ah-arrow">
          <FiChevronRight />
        </span>
      </button>
    </div>
  );
};

export default AttendanceHome;
